import React, { useState, useEffect } from 'react';
import { get, post, patch } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import './ListUsers.css';

const ListUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditSuccess, setCreditSuccess] = useState(null);
  const [nickname, setNickname] = useState('');
  const [vipStatus, setVipStatus] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesError, setAttributesError] = useState(null);
  const [attributesSuccess, setAttributesSuccess] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [notificationSuccess, setNotificationSuccess] = useState(null);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/users',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUserStatus = (user) => {
    if (!user.enabled) return 'Disabled';
    return user.status || 'Unknown';
  };

  const getUserStatusClass = (user) => {
    if (!user.enabled) return 'status-disabled';
    if (user.status === 'CONFIRMED') return 'status-active';
    if (user.status === 'UNCONFIRMED') return 'status-pending';
    return 'status-unknown';
  };

  const openCreditModal = (user) => {
    setSelectedUser(user);
    setCreditAmount('');
    setCreditDescription('');
    setCreditError(null);
    setCreditSuccess(null);
    setShowCreditModal(true);
  };

  const closeCreditModal = () => {
    setShowCreditModal(false);
    setSelectedUser(null);
    setCreditAmount('');
    setCreditDescription('');
    setCreditError(null);
    setCreditSuccess(null);
  };

  const adjustCredits = async (amount) => {
    if (!selectedUser) return;

    try {
      setCreditLoading(true);
      setCreditError(null);
      setCreditSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setCreditError('Authentication token not found. Please log in again.');
        return;
      }

      const description = creditDescription || (amount > 0 ? 'Admin credit bonus' : 'Admin credit adjustment');

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/rewards/credits',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: {
            user_id: selectedUser.sub,
            amount: amount,
            description: description,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setCreditSuccess(`Successfully ${amount > 0 ? 'added' : 'deducted'} ${Math.abs(amount)} credits. New balance: ${data.new_balance}`);
      setCreditAmount('');
      setCreditDescription('');

      // Refresh users list to show updated credits
      setTimeout(() => {
        fetchUsers();
      }, 1000);

    } catch (err) {
      console.error('Error adjusting credits:', err);
      setCreditError(err.message || 'Failed to adjust credits. Please try again.');
    } finally {
      setCreditLoading(false);
    }
  };

  const handleQuickAdjust = (amount) => {
    adjustCredits(amount);
  };

  const handleCustomAdjust = () => {
    const amount = parseInt(creditAmount, 10);
    if (isNaN(amount) || amount === 0) {
      setCreditError('Please enter a valid amount');
      return;
    }
    adjustCredits(amount);
  };

  const openAttributesModal = (user) => {
    setSelectedUser(user);
    setNickname(user.nickname || '');
    setVipStatus(user.vip === 'true');
    setAttributesError(null);
    setAttributesSuccess(null);
    setShowAttributesModal(true);
  };

  const closeAttributesModal = () => {
    setShowAttributesModal(false);
    setSelectedUser(null);
    setNickname('');
    setVipStatus(false);
    setAttributesError(null);
    setAttributesSuccess(null);
  };

  const updateUserAttributes = async () => {
    if (!selectedUser) return;

    try {
      setAttributesLoading(true);
      setAttributesError(null);
      setAttributesSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setAttributesError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/users/${selectedUser.username}`,
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: {
            nickname: nickname,
            vip: vipStatus,
          },
        },
      });

      const response = await restOperation.response;
      await response.body.json();

      setAttributesSuccess('User attributes updated successfully!');

      // Refresh users list to show updated attributes
      setTimeout(() => {
        fetchUsers();
      }, 1000);

    } catch (err) {
      console.error('Error updating user attributes:', err);
      setAttributesError(err.message || 'Failed to update user attributes. Please try again.');
    } finally {
      setAttributesLoading(false);
    }
  };

  const openNotificationModal = (user) => {
    setSelectedUser(user);
    setNotificationTitle('');
    setNotificationMessage('');
    setNotificationError(null);
    setNotificationSuccess(null);
    setShowNotificationModal(true);
  };

  const closeNotificationModal = () => {
    setShowNotificationModal(false);
    setSelectedUser(null);
    setNotificationTitle('');
    setNotificationMessage('');
    setNotificationError(null);
    setNotificationSuccess(null);
  };

  const sendNotification = async () => {
    if (!selectedUser) return;

    if (!notificationTitle.trim()) {
      setNotificationError('Title is required');
      return;
    }

    if (!notificationMessage.trim()) {
      setNotificationError('Message is required');
      return;
    }

    try {
      setNotificationLoading(true);
      setNotificationError(null);
      setNotificationSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setNotificationError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/notifications/send-push',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: {
            user_id: selectedUser.phone,
            title: notificationTitle,
            message: notificationMessage,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      const pushStatus = data.push_sent
        ? ` Push sent to ${data.devices_notified}/${data.total_devices} device(s).`
        : ' (No devices registered for push)';

      setNotificationSuccess(`Notification sent successfully!${pushStatus}`);
      setNotificationTitle('');
      setNotificationMessage('');

    } catch (err) {
      console.error('Error sending notification:', err);
      setNotificationError(err.message || 'Failed to send notification. Please try again.');
    } finally {
      setNotificationLoading(false);
    }
  };

  return (
    <div className="list-users-container">
      <div className="page-header-section">
        <h2>User Management</h2>
        <p>Manage restaurant users and access</p>
      </div>

      <div className="content-header">
        <h3>All Users</h3>
        <button onClick={fetchUsers} className="refresh-button" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && !error ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Nickname</th>
                  <th>VIP</th>
                  <th>Status</th>
                  <th>Rewards</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.username || index}>
                    <td className="phone-cell">{user.phone || 'N/A'}</td>
                    <td className="nickname-cell">
                      {user.nickname ? (
                        <span className="nickname-text">{user.nickname}</span>
                      ) : (
                        <span className="no-nickname">-</span>
                      )}
                    </td>
                    <td>
                      {user.vip === 'true' ? (
                        <span className="vip-badge">⭐ VIP</span>
                      ) : (
                        <span className="regular-badge">Regular</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${getUserStatusClass(user)}`}>
                        {getUserStatus(user)}
                      </span>
                    </td>
                    <td className="credits-cell">
                      <span className="credits-badge">{user.credits || 0} credits</span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-button edit-btn"
                          onClick={() => openAttributesModal(user)}
                        >
                          Edit
                        </button>
                        <button
                          className="action-button adjust-credits-btn"
                          onClick={() => openCreditModal(user)}
                        >
                          Credits
                        </button>
                        <button
                          className="action-button notify-btn"
                          onClick={() => openNotificationModal(user)}
                        >
                          Notify
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <div className="users-count">
        Total Users: <strong>{users.length}</strong>
      </div>

      {/* Credit Adjustment Modal */}
      {showCreditModal && selectedUser && (
        <div className="modal-overlay" onClick={closeCreditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Credits for {selectedUser.username || selectedUser.phone}</h3>
              <button className="close-button" onClick={closeCreditModal}>×</button>
            </div>

            <div className="modal-body">
              {creditError && (
                <div className="error-banner">
                  {creditError}
                </div>
              )}

              {creditSuccess && (
                <div className="success-banner">
                  {creditSuccess}
                </div>
              )}

              <div className="quick-actions">
                <h4>Quick Adjustments</h4>
                <div className="quick-buttons">
                  <button
                    className="quick-btn add-btn"
                    onClick={() => handleQuickAdjust(10)}
                    disabled={creditLoading}
                  >
                    +10
                  </button>
                  <button
                    className="quick-btn add-btn"
                    onClick={() => handleQuickAdjust(50)}
                    disabled={creditLoading}
                  >
                    +50
                  </button>
                  <button
                    className="quick-btn add-btn"
                    onClick={() => handleQuickAdjust(100)}
                    disabled={creditLoading}
                  >
                    +100
                  </button>
                  <button
                    className="quick-btn subtract-btn"
                    onClick={() => handleQuickAdjust(-10)}
                    disabled={creditLoading}
                  >
                    -10
                  </button>
                  <button
                    className="quick-btn subtract-btn"
                    onClick={() => handleQuickAdjust(-50)}
                    disabled={creditLoading}
                  >
                    -50
                  </button>
                </div>
              </div>

              <div className="custom-adjustment">
                <h4>Custom Amount</h4>
                <div className="form-group">
                  <label>Amount (use negative for deduction):</label>
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="e.g., 25 or -15"
                    disabled={creditLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Description (optional):</label>
                  <input
                    type="text"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    placeholder="e.g., Welcome bonus"
                    disabled={creditLoading}
                  />
                </div>
                <button
                  className="submit-button"
                  onClick={handleCustomAdjust}
                  disabled={creditLoading || !creditAmount}
                >
                  {creditLoading ? 'Processing...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Attributes Modal */}
      {showAttributesModal && selectedUser && (
        <div className="modal-overlay" onClick={closeAttributesModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User Attributes: {selectedUser.username || selectedUser.phone}</h3>
              <button className="close-button" onClick={closeAttributesModal}>×</button>
            </div>

            <div className="modal-body">
              {attributesError && (
                <div className="error-banner">
                  {attributesError}
                </div>
              )}

              {attributesSuccess && (
                <div className="success-banner">
                  {attributesSuccess}
                </div>
              )}

              <div className="attributes-form">
                <div className="form-group">
                  <label>Nickname:</label>
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Enter a custom nickname"
                    disabled={attributesLoading}
                    maxLength={50}
                  />
                  <small className="help-text">Optional custom display name for this user</small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={vipStatus}
                      onChange={(e) => setVipStatus(e.target.checked)}
                      disabled={attributesLoading}
                    />
                    <span className="checkbox-text">Mark as VIP Member</span>
                  </label>
                  <small className="help-text">VIP members get special recognition and benefits</small>
                </div>

                <div className="modal-actions">
                  <button
                    className="cancel-button"
                    onClick={closeAttributesModal}
                    disabled={attributesLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="submit-button"
                    onClick={updateUserAttributes}
                    disabled={attributesLoading}
                  >
                    {attributesLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Modal */}
      {showNotificationModal && selectedUser && (
        <div className="modal-overlay" onClick={closeNotificationModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Push Notification to {selectedUser.nickname || selectedUser.phone}</h3>
              <button className="close-button" onClick={closeNotificationModal}>×</button>
            </div>

            <div className="modal-body">
              {notificationError && (
                <div className="error-banner">
                  {notificationError}
                </div>
              )}

              {notificationSuccess && (
                <div className="success-banner">
                  {notificationSuccess}
                </div>
              )}

              <div className="notification-form">
                <div className="form-group">
                  <label>Notification Title:</label>
                  <input
                    type="text"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    placeholder="e.g., Special Offer"
                    disabled={notificationLoading}
                    maxLength={100}
                  />
                  <small className="help-text">Short title for the notification (max 100 characters)</small>
                </div>

                <div className="form-group">
                  <label>Message:</label>
                  <textarea
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    placeholder="Enter your message here..."
                    disabled={notificationLoading}
                    rows={4}
                    maxLength={500}
                  />
                  <small className="help-text">
                    Notification message (max 500 characters). {500 - notificationMessage.length} characters remaining.
                  </small>
                </div>

                <div className="modal-actions">
                  <button
                    className="cancel-button"
                    onClick={closeNotificationModal}
                    disabled={notificationLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="submit-button"
                    onClick={sendNotification}
                    disabled={notificationLoading || !notificationTitle.trim() || !notificationMessage.trim()}
                  >
                    {notificationLoading ? 'Sending...' : 'Send Notification'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListUsers;
